import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { StressFaceIcon } from '../../components/sessionResult/StressFaceIcons';
import {
  KakaoIcon,
  MetricIconClock,
  MetricIconHeart,
  MetricIconHrv,
} from '../../components/sessionResult/SessionMetricIcons';
import {
  CognitiveFootprintsDecoration,
  CognitiveIconMemory,
  CognitiveIconReasoning,
  CognitiveIconVisualSearch,
  CognitiveMedalDecoration,
} from '../../components/sessionResult/CognitiveMetricIcons';
import sessionCompleteIcon from '../../assets/images/sessionResult/sessionCompleteIcon.png';
import {
  displayMetric,
  isKakaoShareEnabled,
  parseSessionResultQuery,
  type SessionResultQueryV1,
} from '../../utils/sessionResult/parseSessionResultQuery';
import {
  displayScorePair,
  formatSessionDurationSec,
  parseCognitiveSessionResultQuery,
  type CognitiveSessionResultQueryV1,
} from '../../utils/sessionResult/parseCognitiveSessionResultQuery';
import {
  displayMoodScore,
  moodScorePercent,
  parseMindRestSessionResultQuery,
  type MindRestSessionResultQueryV1,
} from '../../utils/sessionResult/parseMindRestSessionResultQuery';
import {
  formatSessionRecordDate,
  getStressTheme,
} from '../../utils/sessionResult/stressTheme';
import {
  buildMindRestKakaoShareUrl,
  getSessionResultShareUrl,
  sessionShareFeedbackMessage,
  shareSessionResultPage,
} from '../../utils/sessionResult/shareSessionResultPage';
import {
  SESSION_RESULT_OG_IMAGE,
  SESSION_RESULT_OG_IMAGE_HEIGHT,
  SESSION_RESULT_OG_IMAGE_WIDTH,
  SESSION_RESULT_SITE_NAME,
} from '../../utils/sessionResult/sessionResultSeo';
import { openSoundPillApp } from '../../utils/openSoundPillApp';
import './session-result-page.css';

type VitalCardProps = {
  icon: ReactNode;
  iconBg: string;
  ringColor: string;
  label: string;
  value: ReactNode;
  subLabel?: string;
  accent: string;
};

function VitalCard({ icon, iconBg, ringColor, label, value, subLabel, accent }: VitalCardProps) {
  return (
    <article className="qr-session-result__card">
      <div className="qr-session-result__card-icon" style={{ background: iconBg }}>
        {icon}
      </div>
      <div className="qr-session-result__card-ring" style={{ borderColor: ringColor }} aria-hidden />
      <div className="qr-session-result__card-body">
        <p className="qr-session-result__card-label">{label}</p>
        <div className="qr-session-result__card-value">{value}</div>
        {subLabel ? (
          <p className="qr-session-result__card-sub" style={{ color: accent }}>
            {subLabel}
          </p>
        ) : null}
      </div>
    </article>
  );
}

function SessionResultEmpty() {
  return (
    <div className="qr-session-result qr-session-result--empty">
      <Helmet>
        <title>세션 결과 | SoundPill</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <h1>세션 결과를 찾을 수 없어요</h1>
      <p>QR 코드로 다시 스캔하거나, 키오스크에서 세션을 완료한 뒤 공유해 주세요.</p>
      <Link to="/app/home">사운드필 홈으로</Link>
    </div>
  );
}

type ShareShellProps = {
  pageTitle: string;
  shareDescription: string;
  shareUrl: string;
  ogImageAlt: string;
  autoKakaoShare: boolean;
  kakaoTitle: string;
  kakaoDescription: string;
  ctaLead: string;
  ctaTitle?: string;
  contentClassName?: string;
  children: ReactNode;
  themeStyle?: CSSProperties;
};

function SessionResultShareShell({
  pageTitle,
  shareDescription,
  shareUrl,
  ogImageAlt,
  autoKakaoShare,
  kakaoTitle,
  kakaoDescription,
  ctaLead,
  ctaTitle = '사운드필에서 계속하세요',
  contentClassName,
  children,
  themeStyle,
}: ShareShellProps) {
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const autoShareTriggered = useRef(false);

  const handleKakaoShare = useCallback(async () => {
    const result = await shareSessionResultPage({
      title: kakaoTitle,
      description: kakaoDescription,
      url: shareUrl,
      imageUrl: SESSION_RESULT_OG_IMAGE,
    });
    const message = sessionShareFeedbackMessage(result);
    setShareFeedback(message);
    if (message) {
      window.setTimeout(() => setShareFeedback(null), 4000);
    }
  }, [kakaoDescription, kakaoTitle, shareUrl]);

  useEffect(() => {
    if (!autoKakaoShare || autoShareTriggered.current) return;
    autoShareTriggered.current = true;
    void handleKakaoShare();
  }, [autoKakaoShare, handleKakaoShare]);

  return (
    <div className="qr-session-result" style={themeStyle}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={shareDescription} />
        <meta name="robots" content="noindex" />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SESSION_RESULT_SITE_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:image" content={SESSION_RESULT_OG_IMAGE} />
        <meta property="og:image:secure_url" content={SESSION_RESULT_OG_IMAGE} />
        <meta property="og:image:width" content={String(SESSION_RESULT_OG_IMAGE_WIDTH)} />
        <meta property="og:image:height" content={String(SESSION_RESULT_OG_IMAGE_HEIGHT)} />
        <meta property="og:image:alt" content={ogImageAlt} />
        <meta property="og:locale" content="ko_KR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={shareDescription} />
        <meta name="twitter:image" content={SESSION_RESULT_OG_IMAGE} />
      </Helmet>

      <div className="qr-session-result__bg" aria-hidden />

      <div className="qr-session-result__inner">
        <div
          className={
            contentClassName
              ? `qr-session-result__content ${contentClassName}`
              : 'qr-session-result__content'
          }
        >
          <div className="qr-session-result__hero-icon">
            <img
              src={sessionCompleteIcon}
              alt=""
              className="qr-session-result__hero-icon-img"
            />
          </div>

          {children}

          <button
            type="button"
            className="qr-session-result__kakao"
            onClick={() => {
              void handleKakaoShare();
            }}
          >
            <KakaoIcon />
            <span>카카오톡 공유</span>
          </button>
          {shareFeedback ? (
            <p className="qr-session-result__share-feedback" role="status">
              {shareFeedback}
            </p>
          ) : null}

          <div className="qr-session-result__cta-copy">
            <p className="qr-session-result__cta-lead">{ctaLead}</p>
            <p className="qr-session-result__cta-title">{ctaTitle}</p>
          </div>

          <button
            type="button"
            className="qr-session-result__open-btn"
            onClick={() => openSoundPillApp()}
          >
            사운드필 열기
          </button>

          <p className="qr-session-result__promo">
            의학적 근거를 바탕으로
            <br />
            3년 동안 연구한
            <br />
            사운드필을 경험하세요
          </p>

          <footer className="qr-session-result__footer">
            본 리포트는 SoundPill 키오스크 세션 측정 결과이며, 개인 참고용입니다.
          </footer>
        </div>
      </div>
    </div>
  );
}

type StressContentProps = {
  data: SessionResultQueryV1;
  autoKakaoShare: boolean;
};

function StressSessionResultContent({ data, autoKakaoShare }: StressContentProps) {
  const theme = getStressTheme(data.stress);
  const recordDate = formatSessionRecordDate(data.t);
  const pageTitle = `나의 스트레스 완화 세션 | SoundPill`;
  const shareUrl = getSessionResultShareUrl();
  const shareDescription = data.sl || data.hl;

  const themeStyle = {
    '--qr-hero-glow': theme.heroGlow,
    '--qr-hero-shadow': theme.heroShadow,
    '--qr-accent': theme.accent,
    '--qr-icon-bg': theme.iconBg,
    '--qr-ring-color': theme.ringColor,
  } as CSSProperties;

  return (
    <SessionResultShareShell
      pageTitle={pageTitle}
      shareDescription={shareDescription}
      shareUrl={shareUrl}
      ogImageAlt="사운드필 스트레스 완화 세션 결과"
      autoKakaoShare={autoKakaoShare}
      kakaoTitle="나의 스트레스 완화 세션"
      kakaoDescription="사운드필 키오스크에서 측정한 스트레스 세션 결과입니다."
      ctaLead="스트레스 세션, 나도 해보고 싶다면?"
      themeStyle={themeStyle}
    >
      <header className="qr-session-result__header">
        <h1 className="qr-session-result__title">
          나의 스트레스 완화
          <br />
          세션 기록
        </h1>
        {recordDate ? <p className="qr-session-result__date">{recordDate}</p> : null}
      </header>

      <section className="qr-session-result__cards" aria-label="측정 요약">
        <VitalCard
          icon={<StressFaceIcon level={theme.level} size={32} />}
          iconBg={theme.iconBg}
          ringColor={theme.ringColor}
          label="스트레스 지수"
          value={data.stress}
          subLabel={theme.stressSubLabel}
          accent={theme.accent}
        />
        <VitalCard
          icon={<MetricIconHeart size={28} />}
          iconBg="#f3ebe4"
          ringColor="#f3ebe4"
          label="심박수"
          value={
            <>
              {displayMetric(data.bpm)}
              <span className="qr-session-result__unit">bpm</span>
            </>
          }
          subLabel={data.note || undefined}
          accent="#006c49"
        />
        <VitalCard
          icon={<MetricIconHrv size={30} />}
          iconBg="#eaf9f7"
          ringColor="#eaf9f7"
          label="HRV"
          value={displayMetric(data.hrv)}
          subLabel="안정됨"
          accent="#006c49"
        />
        <VitalCard
          icon={<MetricIconClock size={28} />}
          iconBg="#e6edf8"
          ringColor="#e6edf8"
          label="세션 시간"
          value={
            <>
              {displayMetric(data.min)}
              <span className="qr-session-result__unit">min</span>
            </>
          }
          accent={theme.accent}
        />
      </section>
    </SessionResultShareShell>
  );
}

type CognitiveScoreCardProps = {
  icon: ReactNode;
  iconBg: string;
  iconBorder: string;
  title: string;
  description: string;
  correct: string;
  total: string;
  scoreColor: string;
};

function CognitiveScoreCard({
  icon,
  iconBg,
  iconBorder,
  title,
  description,
  correct,
  total,
  scoreColor,
}: CognitiveScoreCardProps) {
  return (
    <article className="qr-cognitive-card">
      <div className="qr-cognitive-card__main">
        <div
          className="qr-cognitive-card__icon"
          style={{ background: iconBg, borderColor: iconBorder }}
        >
          {icon}
        </div>
        <div className="qr-cognitive-card__copy">
          <p className="qr-cognitive-card__title">{title}</p>
          <p className="qr-cognitive-card__desc">{description}</p>
        </div>
      </div>
      <div className="qr-cognitive-card__score">
        <span className="qr-cognitive-card__score-main" style={{ color: scoreColor }}>
          {correct}
        </span>
        <span className="qr-cognitive-card__score-total">/ {total}</span>
      </div>
    </article>
  );
}

type CognitiveContentProps = {
  data: CognitiveSessionResultQueryV1;
  autoKakaoShare: boolean;
};

function CognitiveSessionResultContent({ data, autoKakaoShare }: CognitiveContentProps) {
  const recordDate = formatSessionRecordDate(data.t);
  const durationLabel = formatSessionDurationSec(data.sec);
  const pageTitle = `나의 인지능력 강화 기록 | SoundPill`;
  const shareUrl = getSessionResultShareUrl();
  const shareDescription = '사운드필 키오스크에서 진행한 인지능력 강화 세션 결과입니다.';

  const visual = displayScorePair(data.visualSearch);
  const memory = displayScorePair(data.memory);
  const reasoning = displayScorePair(data.reasoning);

  return (
    <SessionResultShareShell
      pageTitle={pageTitle}
      shareDescription={shareDescription}
      shareUrl={shareUrl}
      ogImageAlt="사운드필 인지능력 강화 세션 결과"
      autoKakaoShare={autoKakaoShare}
      kakaoTitle="나의 인지능력 강화 기록"
      kakaoDescription={shareDescription}
      ctaLead="인지기능 강화 사운드"
      ctaTitle="사운드필에서 들어보세요"
      contentClassName="qr-session-result__content--cognitive"
    >
      <header className="qr-session-result__header qr-session-result__header--cognitive">
        <h1 className="qr-session-result__title qr-session-result__title--cognitive">
          나의 인지능력 강화 기록
        </h1>
        {recordDate ? <p className="qr-session-result__date">{recordDate}</p> : null}
        {durationLabel ? (
          <p className="qr-session-result__duration">세션 시간 {durationLabel}</p>
        ) : null}
      </header>

      <div className="qr-cognitive-banner">
        <CognitiveMedalDecoration className="qr-cognitive-banner__medal" />
        <p className="qr-cognitive-banner__title">오늘의 두뇌 한 걸음</p>
        <CognitiveFootprintsDecoration className="qr-cognitive-banner__footprints" />
      </div>

      <section className="qr-session-result__cards qr-session-result__cards--cognitive" aria-label="인지 훈련 결과">
        <CognitiveScoreCard
          icon={<CognitiveIconVisualSearch />}
          iconBg="#eaf9f7"
          iconBorder="#006c49"
          title="시각적 탐색 훈련"
          description="빠르게 목표를 잘 찾아냈어요!"
          correct={visual.correct}
          total={visual.total}
          scoreColor="#006c49"
        />
        <CognitiveScoreCard
          icon={<CognitiveIconMemory />}
          iconBg="#e6edf8"
          iconBorder="#2563eb"
          title="기억력 강화"
          description="도형과 위치를 잘 기억했어요!"
          correct={memory.correct}
          total={memory.total}
          scoreColor="#2563eb"
        />
        <CognitiveScoreCard
          icon={<CognitiveIconReasoning />}
          iconBg="#f3ebe4"
          iconBorder="#924700"
          title="추론 훈련"
          description="규칙을 잘 찾아냈어요!"
          correct={reasoning.correct}
          total={reasoning.total}
          scoreColor="#924700"
        />
      </section>
    </SessionResultShareShell>
  );
}

type MoodCompareBarProps = {
  label: string;
  score: number;
  variant: 'before' | 'after';
};

function MoodCompareBar({ label, score, variant }: MoodCompareBarProps) {
  const percent = moodScorePercent(score);
  const display = displayMoodScore(score);

  return (
    <div className={`qr-mindrest-bar qr-mindrest-bar--${variant}`}>
      <p className="qr-mindrest-bar__label">{label}</p>
      <div
        className="qr-mindrest-bar__track"
        role="img"
        aria-label={`${label} ${display}점, 10점 만점`}
      >
        <div className="qr-mindrest-bar__fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="qr-mindrest-bar__value">{display}</p>
    </div>
  );
}

type MindRestContentProps = {
  data: MindRestSessionResultQueryV1;
  autoKakaoShare: boolean;
};

function MindRestSessionResultContent({ data, autoKakaoShare }: MindRestContentProps) {
  const recordDate = formatSessionRecordDate(data.t);
  const pageTitle = `나의 마음 전환 휴식 기록 | SoundPill`;
  // 카카오 링크는 ASCII 단축 URL(?mr=)만 사용 — 한글 쿼리/긴 URL 4002 회피
  const shareUrl = buildMindRestKakaoShareUrl(data);
  const shareDescription = data.sl || data.hl;
  const kakaoTitle = '마음 전환 휴식 결과';
  const kakaoDescription = `기분 ${displayMoodScore(data.before)} → ${displayMoodScore(data.after)}. ${shareDescription}`;

  return (
    <SessionResultShareShell
      pageTitle={pageTitle}
      shareDescription={shareDescription}
      shareUrl={shareUrl}
      ogImageAlt="사운드필 마음 전환 휴식 세션 결과"
      autoKakaoShare={autoKakaoShare}
      kakaoTitle={kakaoTitle}
      kakaoDescription={kakaoDescription}
      ctaLead="기분이 좀 나아지셨다면?"
      ctaTitle="사운드필에서 계속 해보세요"
      contentClassName="qr-session-result__content--mindrest"
    >
      <header className="qr-session-result__header qr-session-result__header--mindrest">
        <h1 className="qr-session-result__title qr-session-result__title--mindrest">{data.hl}</h1>
        {data.sl ? <p className="qr-session-result__mindrest-sub">{data.sl}</p> : null}
        {recordDate ? <p className="qr-session-result__date">{recordDate}</p> : null}
      </header>

      <section className="qr-mindrest-bars" aria-label="기분 변화">
        <MoodCompareBar label="시작 전" score={data.before} variant="before" />
        <MoodCompareBar label="지금" score={data.after} variant="after" />
      </section>
    </SessionResultShareShell>
  );
}

/** QR 스캔용 공개 세션 결과 — 스트레스 / 인지 / 마음 전환 휴식 (로그인 불필요) */
export default function SessionResultPage() {
  const [searchParams] = useSearchParams();
  const autoKakaoShare = isKakaoShareEnabled(searchParams);
  const mindRestData = parseMindRestSessionResultQuery(searchParams);
  const cognitiveData = parseCognitiveSessionResultQuery(searchParams);
  const stressData = parseSessionResultQuery(searchParams);

  if (mindRestData) {
    return <MindRestSessionResultContent data={mindRestData} autoKakaoShare={autoKakaoShare} />;
  }

  if (cognitiveData) {
    return <CognitiveSessionResultContent data={cognitiveData} autoKakaoShare={autoKakaoShare} />;
  }

  if (stressData) {
    return <StressSessionResultContent data={stressData} autoKakaoShare={autoKakaoShare} />;
  }

  return <SessionResultEmpty />;
}
